import merge from 'lodash/merge';
import { CardDataWithHeroExtras, BadgeConfig } from 'flows/core/types/cards';
import { ItemDefinition, ItemKinds } from 'types';
import { DataForCards } from 'flows/core/hooks/use-data-for-cards';
import { EssentialCards } from 'flows/core/types/layouts';
import { getProviderData } from 'flows/insurance/constants/providers';
import { CLOUDINARY_BASE_URL } from 'flows/insurance/constants';
import { ItemStatuses } from 'constants/statuses';
import { enabledItemDefOfKind, isCompleted, itemOfKind, Badges } from './utils';
import { Item } from 'types/item';
import { Direction } from '@updater/consumer-graph';
import { getUserInfo_getUser_user } from 'types/generated/getUserInfo';

/* Static */
const KIND = ItemKinds.INSURANCE;
const PROOF_OF_INSURANCE_URL_PREFIX = '/resident-onboarding/proof-of-insurance';

const PREFERRED_PROVIDER_TEXT = 'Preferred by your property';
const MOVE_OUT_PROVIDER_TEXT =
  'See an instant quote and purchase protection for your new home.';

const INSURANCE_COMMON_STATIC_DATA = {
  subtype: 'SYI',
  icon: 'SHIELD',
  title: 'Secure renter’s insurance', //default headerText (nonUpo)
  action: {
    type: 'internal',
    route: '/insurance/provider',
  },
  completionTimeText: '< 5 min',
} as const;

const INSURANCE_ASSURANT_STATIC_DATA = {
  subtype: 'SYI',
  icon: 'SHIELD',
  title: 'Secure renter’s insurance', //default headerText (nonUpo)
  action: {
    type: 'internal',
    route: '/insurance/assurant',
  },
  description:
    'See an instant quote and purchase protection for your new home.',
} as const;

const INSURANCE_INITIATE_STATIC_DATA = {
  ...INSURANCE_COMMON_STATIC_DATA,
  description:
    'Ensure you have the proper insurance coverage and upload proof.',
} as const;

const INSURANCE_NO_PROOF_INITIATE_STATIC_DATA = {
  ...INSURANCE_COMMON_STATIC_DATA,
  description: 'Ensure you have the proper insurance coverage.',
} as const;

export const statusToBadge = (
  status?: string,
  tab?: string,
  item?: Item
): BadgeConfig | undefined => {
  if (
    status === ItemStatuses.STARTED &&
    tab !== 'today' &&
    item?.metadata?.viewed === true
  ) {
    return Badges.RECENTLY_VIEWED;
  }
  switch (status) {
    case ItemStatuses.UNVERIFIED:
      return Badges.ALMOST_DONE;
    case ItemStatuses.VALIDATING:
      return Badges.IN_REVIEW;
    case ItemStatuses.COMPLETED:
      return Badges.DONE;
    case ItemStatuses.INFORMATION_REQUESTED:
    case ItemStatuses.INFO_REQUESTED:
      return Badges.NEEDS_MORE;
    default:
      return undefined;
  }
};

export const statusToImportance = (status?: string) => {
  if (status === 'started') {
    return 750_001;
  }
  if (status === 'completed') {
    return 0;
  }
  return 1_000_000;
};

export const makeTodayCardJson = (
  status: ItemStatuses,
  commonData: any,
  payloadKind: 'INITIATE' | 'PROMOTED',
  item?: Item
): CardDataWithHeroExtras => {
  return {
    type: payloadKind,
    data: {
      ...commonData,
      badge: statusToBadge(status, 'today', item),
    },
    heroExtras: {
      heading: 'Get insurance coverage to keep your stuff safe. ',
      ctaText: 'Get started',
    },
    identifier: `syi_${payloadKind.toLowerCase()}`,
    importanceScore: statusToImportance(status),
  };
};

export const getCardInfoFromProofItem = ({
  proofOfInsuranceItem,
}: {
  proofOfInsuranceItem: Item;
}): { status: string; route: string } => {
  const status = proofOfInsuranceItem?.status;
  let route = '/insurance/provider';
  switch (proofOfInsuranceItem?.status) {
    case ItemStatuses.COMPLETED:
    case ItemStatuses.VALIDATING:
      route = `${PROOF_OF_INSURANCE_URL_PREFIX}/thank-you`; // TODO:  replace with receipt page
      break;
    case ItemStatuses.INFORMATION_REQUESTED:
    case ItemStatuses.INFO_REQUESTED:
      route = `${PROOF_OF_INSURANCE_URL_PREFIX}/rejection`;
      break;
    default:
      route = '/insurance/landing';

      break;
  }
  return { status: status as string, route };
};

export const getCardRoute = (item: Item): string => {
  if (
    item?.metadata &&
    Object.prototype.hasOwnProperty.call(
      item?.metadata,
      'completed_insurance_type'
    )
  ) {
    switch (item?.metadata?.completed_insurance_type) {
      case 'assurant_pol':
        return 'insurance/assurant/standard/receipt';
      case 'assurant_poi':
        return 'insurance/assurant/upload/receipt';
      default:
        return 'insurance/provider';
    }
  }
  return 'insurance/provider';
};

/* TODAY CARD */
export const makeTodayTabInsuranceCards = (
  { itemDefinitions, items, user, isGreystarPilotEnabled }: DataForCards,
  plansData?: any
): CardDataWithHeroExtras[] => {
  const itemDef = itemDefinitions?.find(enabledItemDefOfKind(KIND));
  const proofOfInsuranceItemDef = itemDefinitions.find(
    enabledItemDefOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
  );
  const insuranceItems = items.filter(itemOfKind(KIND));
  if (user?.currentMove?.toAddress.ownership === 'OWN') return [];

  const isMoveOut = user?.currentMove?.direction === Direction.move_out;
  if (!plansData) return [];
  if (itemDef) {
    const [proofOfInsuranceItem] = items.filter(
      itemOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
    );

    const { status, route } = proofOfInsuranceItemDef
      ? getCardInfoFromProofItem({
          proofOfInsuranceItem: proofOfInsuranceItem as unknown as Item,
        })
      : {
          status: insuranceItems[0]?.status ?? '',
          route: getCardRoute(insuranceItems[0] as unknown as Item),
        };

    if (isGreystarPilotEnabled) {
      const card = makeTodayCardJson(
        status as ItemStatuses,
        { ...INSURANCE_ASSURANT_STATIC_DATA },
        'PROMOTED',
        insuranceItems[0] as unknown as Item
      );
      const preferredProviderJson = merge(card, {
        data: {
          image: {
            url: itemDef?.page?.logo_id
              ? `${CLOUDINARY_BASE_URL}${encodeURI(itemDef.page.logo_id)}`
              : null,
            alt: 'Assurant',
          },
          description: PREFERRED_PROVIDER_TEXT,
        },
      });
      return [preferredProviderJson];
    }

    const cardJson = makeTodayCardJson(
      status as ItemStatuses,
      {
        ...INSURANCE_COMMON_STATIC_DATA,
        action: {
          ...INSURANCE_COMMON_STATIC_DATA.action,
          route: isGreystarPilotEnabled ? '/insurance/assurant' : route,
        },
      },
      'PROMOTED',
      insuranceItems[0] as unknown as Item
    );

    const providerData = getProviderData(itemDef?.page?.name || '') || {
      ...itemDef.page,
      logo: itemDef?.page?.logo_id
        ? `${CLOUDINARY_BASE_URL}${encodeURI(itemDef.page.logo_id)}`
        : null,
    };
    const preferredProviderJson = merge(cardJson, {
      data: {
        image: providerData?.logo
          ? { url: providerData.logo, alt: providerData.name }
          : {},
        description: isMoveOut
          ? MOVE_OUT_PROVIDER_TEXT
          : PREFERRED_PROVIDER_TEXT,
      },
    });
    return [preferredProviderJson];
  }

  return [];
};

const essentialsImportanceScore = 1_000_000;
const essentialsType = 'INITIATE';
const essentialsIdentifier = 'syi_initiate';

export const isItemRequired = (
  user: getUserInfo_getUser_user | undefined,
  itemDef: ItemDefinition,
  proofItem: Item
) => {
  if (user?.currentMove?.direction === Direction.move_out) return false;

  return !!proofItem || !!itemDef?.page?.required || !!itemDef?.page?.assurant;
};

/* Essentials Card */
export const makeEssentialsTabInsuranceCards = ({
  itemDefinitions,
  items,
  user,
  isGreystarPilotEnabled,
}: DataForCards): EssentialCards => {
  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  // we always reference just the first insurance item
  const insuranceItem = items.filter(itemOfKind(KIND))[0] as unknown as Item;

  const proofOfInsuranceItemDef = itemDefinitions.find(
    enabledItemDefOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
  );

  // Determine if user is UPO mover - (Different text)
  const isUPOMove = Boolean(user?.currentMove?.residentOnboardingEnabled);

  const essentialCardLists: EssentialCards = {
    toDoCards: [],
    completedCards: [],
  };
  if (itemDef && user?.currentMove?.toAddress.ownership !== 'OWN') {
    const showAssurant = isGreystarPilotEnabled;

    const proofOfInsuranceItem = items.find(
      itemOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
    ) as unknown as Item;

    const { status, route } = proofOfInsuranceItemDef
      ? getCardInfoFromProofItem({
          proofOfInsuranceItem: proofOfInsuranceItem,
        })
      : {
          status: insuranceItem?.status ?? '',
          route: getCardRoute(insuranceItem),
        };

    if (showAssurant) {
      return makeAssurantEssentialsCard({ status });
    }

    // the insurance item is required when:
    //  - user is UPO and POI is required
    //  - user is UPO and Insurance is required
    //  - Insurance is required
    //  - user is moving into one of the Greystar pilot properties
    //  - user is assurant eligible
    const required = isItemRequired(user, itemDef, proofOfInsuranceItem);

    console.log({ required, proofOfInsuranceItem, items });

    /* If user is UPO, the Header is different */
    const headerText = isUPOMove
      ? 'Secure renters insurance'
      : 'Secure renter’s insurance';

    // Set the static data including the derived headerText (title)
    const insuranceStaticData = {
      ...(isUPOMove
        ? { ...INSURANCE_INITIATE_STATIC_DATA, title: headerText }
        : { ...INSURANCE_NO_PROOF_INITIATE_STATIC_DATA, title: headerText }),
      action: {
        ...INSURANCE_INITIATE_STATIC_DATA.action,
        route,
      },
    };
    const targetList = isCompleted({ status }) ? 'completedCards' : 'toDoCards';
    const badge =
      isCompleted({ status }) || proofOfInsuranceItemDef
        ? statusToBadge(status as ItemStatuses, undefined, insuranceItem)
        : undefined;
    const computedCtaText = badge?.variant === 'done' ? 'View task' : undefined;
    essentialCardLists[targetList].push({
      type: essentialsType,
      data: {
        ...insuranceStaticData,
        // TODO: add badge logic back when we decide on implementation for ROB + Assurant
        badge,
        required,
        status,
        ctaText: computedCtaText,
      },
      identifier: `${essentialsIdentifier}${
        isCompleted({ status }) ? '_completed' : ''
      }${insuranceItem ? `_${insuranceItem.id}` : ''}`,
      importanceScore: essentialsImportanceScore,
    });
  }
  return essentialCardLists;
};
export function makeAssurantEssentialsCard({
  status,
}: {
  status: string;
}): EssentialCards {
  const essentialCardLists: EssentialCards = {
    toDoCards: [],
    completedCards: [],
  };
  const targetList = isCompleted({ status }) ? 'completedCards' : 'toDoCards';
  const badge = statusToBadge(status as ItemStatuses);
  essentialCardLists[targetList].push({
    type: essentialsType,
    data: {
      ...INSURANCE_ASSURANT_STATIC_DATA,
      required: true,
      status,
      badge,
      ctaText: badge?.variant === 'done' ? 'View task' : undefined,
    },
    identifier: essentialsIdentifier,
    importanceScore: essentialsImportanceScore,
  });
  return essentialCardLists;
}
